<template>
    <div class="grid">
        <div class="unit whole">
            <h1>View Pupils</h1>
            <v-btn color="brand" class="pull-right mr-5" to="/teacher/add-pupil">Add a new pupil</v-btn>
            <v-btn color="brand" class="pull-right mr-5" to="/teacher/groups">Manage groups</v-btn>
            <p class="lead">View your class and add new pupils.</p>
        </div>
    </div>
    <div class="grid">
        <div class="unit whole">
            <div class="panel filter-panel">
                <div class="d-flex justify-space-between">
                    <div class="d-flex w-50 pb-2">
                        <v-select
                            v-model="selectedClass"
                            class="mr-5 mb-0"
                            density="compact"
                            hide-details
                            label="Class"
                            :items="classes"
                            :clearable="true"
                            :open-on-clear="false"
                            item-title="name"
                            item-value="uid"
                        ></v-select>
                        <v-select
                            v-model="selectedGroup"
                            class="mb-0"
                            density="compact"
                            hide-details
                            label="Group"
                            :items="groups"
                            :clearable="true"
                            :open-on-clear="false"
                            item-title="name"
                            item-value="uid"
                        ></v-select>
                    </div>
                    <div>
                        <v-btn variant="outlined" @click="printAction('login-details')" class="mr-2"
                            >Print login details</v-btn
                        >
                        <v-btn variant="outlined" @click="printAction('login-letters')">Print login letters</v-btn>
                    </div>
                </div>
            </div>
            <div class="panel">
                <v-data-table
                    items-per-page="-1"
                    :headers="headers"
                    :items="items"
                    item-value="id"
                    :sort-by="sortBy"
                    class="elevation-0"
                >
                    <template #bottom></template>
                    <template #[`header.show_audio`]="{ column }">
                        {{ column.title }} <v-icon size="small" icon="mdi-open-in-new"></v-icon>
                        <v-menu
                            offset="10"
                            activator="parent"
                            :open-on-hover="true"
                            :close-on-content-click="false"
                            location="top"
                        >
                            <v-card class="pa-2">
                                <v-card-actions>
                                    <v-switch
                                        v-model="showAudioAll"
                                        hide-details
                                        density="compact"
                                        color="primary"
                                        :indeterminate="showAudioAll === undefined"
                                        @update:model-value="
                                            (value: boolean | null) =>
                                                toggleShowAudio(
                                                    items.map((p) => p.id),
                                                    value,
                                                )
                                        "
                                    >
                                        <template #prepend>Switch all</template>
                                    </v-switch>
                                </v-card-actions>
                            </v-card>
                        </v-menu>
                    </template>
                    <template #[`item.name`]="{ item }">
                        <RouterLink :to="`/teacher/pupil/${item.id}`">{{ item.name }}</RouterLink>
                    </template>
                    <template #[`item.password`]="{ item }">
                        <v-btn
                            v-if="item.id !== teacher?.virtual_pupil.id"
                            size="small"
                            style="height: 32px"
                            variant="outlined"
                            @click="
                                changePasswordPupil = item;
                                showChangePasswordDialog = true;
                            "
                            >Change</v-btn
                        >
                    </template>
                    <template #[`item.book`]="{ item }">
                        <RouterLink v-if="item.book" :to="`/teacher/book/${item.book.id}/about`">{{
                            item.book.title
                        }}</RouterLink>
                    </template>
                    <template #[`item.last_active_details`]="{ item }">{{ item.last_active_details.text }} </template>
                    <template #[`item.login_letter`]="{ item }">
                        <a href="#" @click="printAction('login-letter', item)">
                            <i class="fa fa-envelope"></i>
                            <v-tooltip activator="parent" location="top">Print login letter</v-tooltip>
                        </a>
                    </template>
                    <template #[`item.show_audio`]="{ item }">
                        <template v-if="isTogglingAudio[item.id]">
                            <v-progress-circular
                                indeterminate
                                size="16"
                                width="2"
                                color="primary"
                            ></v-progress-circular>
                        </template>
                        <v-switch
                            v-else
                            v-model="item.show_audio"
                            hide-details
                            class="table-switcher"
                            density="compact"
                            color="primary"
                            @update:model-value="(value: boolean | null) => toggleShowAudio([item.id], value)"
                        ></v-switch>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <v-overlay :model-value="!isLoaded.pupils" class="align-center justify-center">
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-dialog v-model="showChangePasswordDialog" max-width="500px">
        <v-form v-model="passwordFormValid" @submit.prevent="changePassword">
            <v-card title="Change password">
                <v-card-text>
                    Type the new password for {{ changePasswordPupil?.name }}. You will not be able to see the password
                    again.
                    <v-text-field
                        class="mt-3"
                        label="New password"
                        v-model="newPassword"
                        :rules="[rules.required, rules.atLeast8Chars, rules.noSpacesAtEnds]"
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-btn @click="showChangePasswordDialog = false" :disabled="changePasswordInProgress">Cancel</v-btn>
                    <v-btn
                        color="primary"
                        type="submit"
                        :loading="changePasswordInProgress"
                        :disabled="!passwordFormValid"
                        >Change password</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>

    <div id="js_print-login-details" class="d-none">
        <PrintLoginDetails :pupils="items" :school="school" />
    </div>
    <div id="js_print-login-letters" class="d-none">
        <PrintLoginLetters :pupils="items" :school="school" />
    </div>
    <div id="js_print-login-letter" class="d-none">
        <PrintLoginLetters v-if="pupilToPrint" :pupils="[pupilToPrint]" :school="school" />
    </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { DateTime } from 'luxon';
import { VDataTable } from 'vuetify/components/VDataTable';
import { PupilOverview } from '@ilteducation/giglets-types';
import print from '@/utils/print';
import PrintLoginDetails from '@/components/PrintLoginDetails.vue';

definePage({ meta: { area: 'pupils-and-groups' } });
const { formatDate } = useDateFormat();

const store = useTeacherStore();
store.fetchPupils();
store.fetchTeacher();
store.fetchSchool();
const { teacher, school, pupils, isLoaded } = storeToRefs(store);
const { snackbarSuccess } = useSnackbar();
const pupilToPrint = ref<PupilOverview>();

type PupilOverviewWithLastActive = PupilOverview & {
    last_active_details: { timestamp: number; text: string | undefined };
};

const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);
const selectedClass = ref<string>();
const selectedGroup = ref<string>();
const showChangePasswordDialog = ref(false);
const newPassword = ref('');
const changePasswordPupil = ref<PupilOverviewWithLastActive>();
const changePasswordInProgress = ref(false);
const passwordFormValid = ref(true);
const isTogglingAudio = ref<Record<number, boolean>>({});

const rules = {
    required: (value: string) => !!value || 'Required field.',
    atLeast8Chars: (value: string) => {
        return !value || value.length >= 8 || 'Password must be at least 8 characters.';
    },
    noSpacesAtEnds: (value: string) => {
        return !value || value.trim().length === value.length || 'Password cannot start or end with a space.';
    },
};

const items = computed<PupilOverviewWithLastActive[]>(() => {
    return pupils.value
        .filter((p) => {
            if (selectedClass.value && !p.classes.map((p) => p.uid).includes(selectedClass.value)) {
                return false;
            }
            return !(selectedGroup.value && !p.groups.map((p) => p.uid).includes(selectedGroup.value));
        })
        .map((pupil) => {
            let lastActiveText: string | undefined;
            let timestamp = 0;
            if (pupil.last_active) {
                const lastActive = DateTime.fromISO(pupil.last_active);
                if (lastActive.isValid) {
                    timestamp = lastActive.toMillis();
                    const daysAgo = lastActive.diffNow().negate().as('days');
                    if (daysAgo < 1) {
                        lastActiveText = 'Today';
                    } else if (daysAgo < 2) {
                        lastActiveText = 'Yesterday';
                    } else {
                        lastActiveText = formatDate(pupil.last_active);
                    }
                }
            }
            return {
                ...pupil,
                last_active_details: { timestamp, text: lastActiveText },
            };
        });
});

const showAudioAll = computed(() =>
    items.value.map((pupil) => pupil.show_audio).some((val) => val !== items.value[0].show_audio)
        ? undefined
        : items.value[0].show_audio,
);

const groups = computed(() => {
    const res: Array<{ uid: string; name: string }> = [];
    pupils.value.forEach((pupil) => {
        pupil.groups.forEach((g) => {
            if (!res.map((p) => p.uid).includes(g.uid)) {
                res.push(g);
            }
        });
    });
    return res.sort((a, b) => a.name.localeCompare(b.name));
});

const classes = computed(() => {
    const res: Array<{ uid: string; name: string }> = [];
    pupils.value.forEach((pupil) => {
        pupil.classes.forEach((c) => {
            if (!res.map((p) => p.uid).includes(c.uid)) {
                res.push(c);
            }
        });
    });
    return res.sort((a, b) => a.name.localeCompare(b.name));
});

const toggleShowAudio = async (pupilIds: number[], value: boolean | null) => {
    if (value === null) {
        return;
    }
    try {
        const updateAttributes = { show_audio: value };
        const pupilsBatch: Record<string, Partial<PupilOverview>> = {};
        pupilIds.forEach((id) => {
            pupilsBatch[id] = updateAttributes;
            isTogglingAudio.value[id] = true;
        });
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await store.updatePupils(pupilsBatch);
    } finally {
        isTogglingAudio.value = {};
    }
};

const headers = [
    {
        title: 'Name',
        key: 'name',
    },
    { title: 'Username', key: 'username' },
    { title: 'Current book', key: 'book' },
    {
        title: 'Last active',
        key: 'last_active_details',
    },
    {
        title: 'Task audio',
        key: 'show_audio',
        sortable: false,
        width: 80,
    },
    { title: 'Password', key: 'password', sortable: false },
    {
        title: '',
        key: 'login_letter',
        sortable: false,
    },
];

async function changePassword() {
    if (!changePasswordPupil.value) {
        return;
    }
    changePasswordInProgress.value = true;
    try {
        await store.updatePupils({ [changePasswordPupil.value.id]: { password: newPassword.value } });
        snackbarSuccess(`Password changed for ${changePasswordPupil.value.name}.`);
        showChangePasswordDialog.value = false;
    } finally {
        changePasswordInProgress.value = false;
    }
}

watch(showChangePasswordDialog, () => {
    if (showChangePasswordDialog.value) {
        newPassword.value = '';
    }
});

const printAction = (type: string, pupil?: PupilOverview) => {
    pupilToPrint.value = pupil;
    nextTick(() => print(`js_print-${type}`));
};
</script>
<style scoped lang="scss">
:deep(th) {
    vertical-align: revert;
}
:deep(td) {
    text-align: revert;
    vertical-align: revert;
    border-top: revert;
}
.filter-panel {
    min-height: revert;
}
.table-switcher {
    :deep(.v-switch__track) {
        height: 10px;
        width: 18px;
    }
    :deep(.v-switch__thumb) {
        height: 16px;
        width: 16px;
    }
}
</style>
